/**
 * The Field Journal.
 *
 * Janeks: *"Style the Quest view, make it more interactable and add volume.
 * Make it look like game UI, zero flatness please. Dont leave text flowing,
 * everything needs a visual container, add animations."*
 *
 * So: every string sits in a plate, chip, slip, tab or stamp; every plate has
 * a bevel, an inner shadow and a drop shadow; cards lift on hover with a shine
 * sweep; bars fill in; the stack staggers in (the --i custom property is set
 * per card in quest-ui.ts); a quest that is ready pulses and wears a stamp.
 * Worn-metal faces come from the clutter kit in /public/clutter/.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it.
 */
export default `
.bv-quest-body { display: flex; flex-direction: column; gap: 22px; }
.bv-q-section { display: flex; flex-direction: column; gap: 12px; }
.bv-q-ico { display: inline-flex; width: 16px; height: 16px; flex: none; }
.bv-q-ico svg, .bv-q-gauge-ico svg, .bv-q-pushpin svg, .bv-q-empty-ico svg {
  width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round;
}

/* ── Gauges ─────────────────────────────────────────────────────────────── */
.bv-q-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.bv-q-gauge {
  position: relative; display: grid; grid-template-columns: 34px 1fr; grid-template-rows: auto auto; column-gap: 10px;
  align-items: center; padding: 10px 12px; border: 1px solid #120d08; border-radius: var(--radius-well);
  min-height: 76px; overflow: hidden; isolation: isolate;
  background:
    linear-gradient(90deg, rgba(10,6,3,0.78) 0 38%, rgba(10,6,3,0) 78%),
    var(--gauge-art, none) right center / cover no-repeat, #1d150d;
  box-shadow: 0 0 0 1px #5a4326, 0 8px 18px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,222,170,0.18), inset 0 -3px 0 rgba(0,0,0,0.5);
  animation: bv-q-rise 0.5s cubic-bezier(0.22,0.7,0.3,1) backwards;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.bv-q-gauge:hover { transform: translateY(-3px); }
.bv-q-gauge-brass { --gauge-art: url('/clutter/gauge-a.webp'); }
.bv-q-gauge-moss { --gauge-art: url('/clutter/gauge-b.webp'); }
.bv-q-gauge-steel { --gauge-art: url('/clutter/gauge-c.webp'); }
.bv-q-gauge:nth-child(2) { animation-delay: 0.07s; }
.bv-q-gauge:nth-child(3) { animation-delay: 0.14s; }
.bv-q-gauge-ico {
  grid-row: 1 / 3; width: 34px; height: 34px; padding: 7px; box-sizing: border-box; border-radius: 50%;
  color: var(--brass-bright); background: radial-gradient(circle at 35% 30%, #4a3a26, #120d08);
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.7), 0 1px 0 rgba(255,222,170,0.2);
}
.bv-q-gauge-num { font: 800 24px/1 var(--font-display); color: var(--brass-hot); text-shadow: 0 2px 0 rgba(0,0,0,0.8); font-variant-numeric: tabular-nums; }
.bv-q-gauge-label { font: 10px var(--font-technical); letter-spacing: 0.14em; text-transform: uppercase; color: var(--bone-faint); }
.bv-q-gauge-moss .bv-q-gauge-num, .bv-q-gauge-moss .bv-q-gauge-ico { color: var(--moss); }
.bv-q-gauge-steel .bv-q-gauge-num, .bv-q-gauge-steel .bv-q-gauge-ico { color: #9fb0a4; }

/* ── Group banners ──────────────────────────────────────────────────────
   Higgsfield art on the right, title and caption on the dark left. */
.bv-q-heading {
  position: relative; display: flex; align-items: center; justify-content: space-between; margin: 0; min-height: 76px;
  padding: 10px 16px 10px 40px; overflow: hidden; isolation: isolate;
  border: 2px solid #120d08; border-radius: 4px 18px 4px 18px;
  background:
    linear-gradient(90deg, rgba(10,6,3,0.92) 0 30%, rgba(10,6,3,0.35) 62%, rgba(10,6,3,0) 85%),
    var(--banner, none) right center / cover no-repeat, #1a1208;
  box-shadow: 0 0 0 1px var(--brass), 0 10px 22px rgba(0,0,0,0.65), inset 0 2px 0 rgba(255,222,170,0.22), inset 0 -4px 0 rgba(0,0,0,0.55), inset 0 0 30px rgba(0,0,0,0.5);
  animation: bv-q-rise 0.5s cubic-bezier(0.22,0.7,0.3,1) backwards;
}
.bv-q-heading-inhand { --banner: url('/clutter/banner-inhand.webp'); }
.bv-q-heading-jobs { --banner: url('/clutter/banner-jobs.webp'); }
.bv-q-heading-notes { --banner: url('/clutter/banner-notes.webp'); }
/* A hazard-striped tab down the left edge, and a rivet on it. */
.bv-q-heading::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 22px;
  background: repeating-linear-gradient(135deg, #d9a020 0 8px, #17110a 8px 16px);
  border-right: 2px solid #120d08; box-shadow: 3px 0 8px rgba(0,0,0,0.6);
}
.bv-q-heading::after {
  content: ''; position: absolute; left: 6px; top: 50%; width: 10px; height: 10px; margin-top: -5px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f0d9a8 0 12%, #7a5a28 45%, #1a1208 85%); box-shadow: 0 1px 2px rgba(0,0,0,0.9);
}
.bv-q-heading-words { display: flex; flex-direction: column; gap: 3px; }
.bv-q-heading-text {
  font: 800 22px/1 var(--font-display); letter-spacing: 0.1em; text-transform: uppercase; color: var(--brass-hot);
  text-shadow: 0 2px 0 rgba(0,0,0,0.9), 0 0 14px rgba(255,189,92,0.35);
}
.bv-q-heading-cap { font: 700 10px var(--font-technical); letter-spacing: 0.18em; text-transform: uppercase; color: var(--bone-dim); text-shadow: 0 1px 2px rgba(0,0,0,0.9); }
.bv-q-heading-count {
  min-width: 34px; padding: 4px 12px; text-align: center; font: 800 16px var(--font-technical); color: #1a1208;
  background: linear-gradient(180deg, var(--brass-hot), #b8862e); border: 1px solid #120d08; border-radius: 14px;
  box-shadow: 0 3px 0 #4a3210, 0 5px 10px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.5);
}

/* ── Story cards ────────────────────────────────────────────────────────── */
.bv-q-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 16px; }
.bv-q-card {
  position: relative; display: flex; flex-direction: column; gap: 10px; padding: 16px 18px 14px;
  border: 2px solid #120d08; border-radius: var(--radius-panel);
  background:
    linear-gradient(180deg, rgba(255,235,200,0.07), transparent 40%),
    linear-gradient(rgba(14,9,5,0.7), rgba(14,9,5,0.82)),
    url('/clutter/plate.webp') 0 0 / 380px 380px, #1d150d;
  box-shadow: 0 0 0 1px #5a4326, 0 12px 24px rgba(0,0,0,0.65), 4px 6px 16px rgba(0,0,0,0.4),
              inset 0 2px 0 rgba(255,222,170,0.16), inset 0 -4px 0 rgba(0,0,0,0.55), inset 0 0 28px rgba(0,0,0,0.5);
  overflow: hidden; isolation: isolate;
  animation: bv-q-rise 0.55s cubic-bezier(0.22,0.7,0.3,1) backwards; animation-delay: calc(var(--i, 0) * 80ms + 120ms);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.bv-q-card::before {
  content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none; opacity: 0.55;
  background: url('/clutter/grime.webp') 40% 20% / 420px 420px;
}
/* Shine: a diagonal sweep that crosses the card on hover. */
.bv-q-card::after, .bv-q-job::after {
  content: ''; position: absolute; top: 0; bottom: 0; left: -60%; width: 40%; pointer-events: none;
  background: linear-gradient(105deg, transparent, rgba(255,235,200,0.12), transparent);
  transform: skewX(-18deg); transition: left 0.7s ease;
}
.bv-q-card:hover, .bv-q-job:hover { transform: translateY(-3px); }
.bv-q-card:hover::after, .bv-q-job:hover::after { left: 130%; }
.bv-q-card:hover { box-shadow: 0 0 0 1px var(--brass), 0 18px 32px rgba(0,0,0,0.7), 4px 8px 20px rgba(0,0,0,0.45),
              inset 0 2px 0 rgba(255,222,170,0.2), inset 0 -4px 0 rgba(0,0,0,0.55), inset 0 0 28px rgba(0,0,0,0.5); }
.bv-q-ready { animation: bv-q-rise 0.55s cubic-bezier(0.22,0.7,0.3,1) backwards, bv-q-glow 2.2s ease-in-out 0.8s infinite; }
.bv-q-rivet {
  position: absolute; width: 9px; height: 9px; border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle at 35% 30%, #f0d9a8 0 12%, #7a5a28 45%, #1a1208 85%);
  box-shadow: 0 1px 2px rgba(0,0,0,0.8);
}
.bv-q-rivet-tl { top: 6px; left: 6px; } .bv-q-rivet-tr { top: 6px; right: 6px; }
.bv-q-rivet-bl { bottom: 6px; left: 6px; } .bv-q-rivet-br { bottom: 6px; right: 6px; }

.bv-q-card-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 0 12px; }
.bv-q-card-title {
  margin: 0; font: 800 17px/1.15 var(--font-display); letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--brass-hot); text-shadow: 0 2px 0 rgba(0,0,0,0.85);
}
.bv-q-step {
  flex: none; padding: 3px 10px; font: 800 12px var(--font-technical); color: var(--bone);
  background: #120d08; border: 1px solid var(--brass); border-radius: 12px; box-shadow: inset 0 2px 4px rgba(0,0,0,0.8);
}
.bv-q-brief {
  margin: 0; padding: 9px 12px; font: 13px/1.5 var(--font-body); color: var(--bone);
  background: linear-gradient(rgba(8,5,3,0.8), rgba(8,5,3,0.86)), url('/clutter/plate.webp') 0 0 / 300px 300px; border: 1px solid rgba(122,90,48,0.35); border-left: 3px solid var(--brass);
  border-radius: var(--radius-well); box-shadow: inset 0 2px 8px rgba(0,0,0,0.6);
}

/* Giver chip */
.bv-q-giver {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 8px; margin: 0 12px; padding: 3px 12px 3px 3px;
  background: linear-gradient(rgba(44,33,22,0.7), rgba(21,15,8,0.85)), url('/clutter/plate.webp') 0 0 / 300px 300px; border: 1px solid #5a4326; border-radius: 16px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,222,170,0.12);
}
.bv-q-giver-disc {
  display: grid; place-items: center; width: 24px; height: 24px; border-radius: 50%;
  font: 800 12px var(--font-display); color: #1a1208;
  background: radial-gradient(circle at 35% 28%, var(--brass-hot), #8a6320); box-shadow: inset 0 -2px 3px rgba(70,40,8,0.6), 0 1px 2px rgba(0,0,0,0.6);
}
.bv-q-giver-name { font: 700 10px var(--font-technical); letter-spacing: 0.12em; text-transform: uppercase; color: var(--bone-dim); }

/* Objectives: each is its own slot */
.bv-q-objs { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 7px; }
.bv-q-obj {
  position: relative; display: grid; grid-template-columns: 24px 1fr auto; column-gap: 9px; row-gap: 5px; align-items: center;
  padding: 8px 11px; background: linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.4)), url('/clutter/plate.webp') 0 0 / 300px 300px;
  border: 1px solid rgba(122,90,48,0.4); border-radius: var(--radius-well); box-shadow: inset 0 2px 6px rgba(0,0,0,0.6);
  animation: bv-q-slide 0.4s ease backwards; animation-delay: calc(var(--j, 0) * 70ms + 380ms);
  transition: border-color 0.15s ease, background 0.15s ease;
}
.bv-q-obj:hover { border-color: var(--brass); }
.bv-q-checkbox {
  display: grid; place-items: center; width: 22px; height: 22px; border-radius: 5px; color: transparent;
  background: #0d0905; border: 1px solid #5a4326; box-shadow: inset 0 2px 4px rgba(0,0,0,0.8);
}
.bv-q-checkbox svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 26; stroke-dashoffset: 26; }
.bv-q-obj-done .bv-q-checkbox { color: #14180c; background: linear-gradient(160deg, var(--moss), var(--moss-dark)); border-color: var(--moss); box-shadow: 0 0 8px rgba(120,170,80,0.4), inset 0 1px 0 rgba(255,255,255,0.35); }
.bv-q-obj-done .bv-q-checkbox svg { animation: bv-q-draw 0.45s ease 0.6s forwards; }
.bv-q-obj-label { font: 13px/1.3 var(--font-body); color: var(--bone); min-width: 0; }
.bv-q-obj-done .bv-q-obj-label { color: var(--moss); }
.bv-q-obj-count { font: 800 13px var(--font-technical); color: var(--brass-hot); font-variant-numeric: tabular-nums; }
.bv-q-bar {
  grid-column: 1 / -1; display: block; height: 7px; overflow: hidden; border-radius: 4px; background: #0d0905;
  border: 1px solid rgba(122,90,48,0.45); box-shadow: inset 0 2px 3px rgba(0,0,0,0.85);
}
.bv-q-fill {
  display: block; height: 100%; width: calc(var(--p, 0) * 100%); border-radius: 3px; transform-origin: left;
  background: linear-gradient(180deg, #ffd98c, var(--brass-hot) 45%, #8a6320);
  box-shadow: 0 0 8px rgba(255,189,92,0.55), inset 0 1px 0 rgba(255,255,255,0.5);
  animation: bv-q-fill 0.9s cubic-bezier(0.22,0.7,0.3,1) 0.5s backwards;
}
.bv-q-obj-done .bv-q-fill, .bv-q-ready .bv-q-fill { background: linear-gradient(180deg, #b6d98a, var(--moss) 45%, var(--moss-dark)); box-shadow: 0 0 8px rgba(120,170,80,0.5), inset 0 1px 0 rgba(255,255,255,0.4); }

.bv-q-owed {
  margin: 0; padding: 8px 12px; font: 12px/1.4 var(--font-technical); color: #e8c8a0;
  background: linear-gradient(180deg, rgba(150,66,26,0.28), rgba(80,30,10,0.35)); border: 1px solid rgba(150,66,26,0.6);
  border-radius: var(--radius-well); box-shadow: inset 0 2px 6px rgba(0,0,0,0.5);
}

/* ── Pay tray ───────────────────────────────────────────────────────────── */
.bv-q-pays {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 7px 9px;
  background: linear-gradient(rgba(8,5,3,0.8), rgba(8,5,3,0.86)), url('/clutter/plate.webp') 0 0 / 300px 300px; border: 1px solid rgba(122,90,48,0.4); border-radius: var(--radius-well);
  box-shadow: inset 0 3px 8px rgba(0,0,0,0.7);
}
.bv-q-pays-label { font: 800 10px var(--font-technical); letter-spacing: 0.16em; text-transform: uppercase; color: var(--bone-faint); margin-right: 2px; }
.bv-q-chip {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px 3px 6px; font: 700 12px var(--font-technical); color: var(--bone);
  background: linear-gradient(rgba(58,44,28,0.72), rgba(26,18,8,0.85)), url('/clutter/plate.webp') 0 0 / 300px 300px; border: 1px solid #5a4326; border-radius: 14px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,222,170,0.14);
  transition: transform 0.14s ease, border-color 0.14s ease;
}
.bv-q-chip:hover { transform: translateY(-1px); border-color: var(--brass-hot); }
.bv-q-chip-xp { color: #9fd0e8; } .bv-q-chip-rep { color: #d9b0e8; } .bv-q-chip-item { color: var(--bone-dim); }
.bv-q-chip .bv-q-ico { color: currentColor; }
.bv-q-chip-cash { color: var(--brass-hot); padding-left: 4px; }

/* ── Buttons: chunky, pressable ─────────────────────────────────────────── */
.bv-q-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 2px; }
.bv-q-btn {
  touch-action: manipulation;
  position: relative; min-height: 44px; padding: 0 20px; cursor: pointer;
  font: 800 12px var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  border: 1px solid #120d08; border-radius: var(--radius-well);
  transition: transform 0.09s ease, filter 0.12s ease, box-shadow 0.09s ease;
}
.bv-q-btn-brass, .bv-q-btn-moss {
  color: #1a1208; text-shadow: 0 1px 0 rgba(255,255,255,0.4);
  box-shadow: 0 5px 0 #4a3210, 0 8px 14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.5);
}
.bv-q-btn-brass { background: linear-gradient(180deg, var(--brass-hot), #b8862e 60%, #8a6320); }
.bv-q-btn-moss { background: linear-gradient(180deg, #b6d98a, var(--moss) 55%, var(--moss-dark)); box-shadow: 0 5px 0 #2a3a14, 0 8px 14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.5); }
.bv-q-btn-quiet { color: var(--bone-faint); background: linear-gradient(180deg, #2a2016, #150f08); border-color: #3a2c1c; box-shadow: 0 3px 0 #0d0905, inset 0 1px 0 rgba(255,222,170,0.08); }
.bv-q-btn:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.1); }
.bv-q-btn:active:not(:disabled) { transform: translateY(3px); box-shadow: 0 1px 0 #4a3210, 0 2px 6px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.3); }
.bv-q-btn:disabled { cursor: not-allowed; opacity: 0.45; }
.bv-q-btn-moss:not(:disabled) { animation: bv-q-pulse 1.6s ease-in-out infinite; }

/* ── Stamps ─────────────────────────────────────────────────────────────── */
.bv-q-stamp {
  display: inline-block; padding: 2px 12px; font: 800 14px var(--font-display); letter-spacing: 0.2em; text-transform: uppercase;
  border: 2px solid currentColor; border-radius: 4px; opacity: 0.92; mix-blend-mode: normal;
}
.bv-q-stamp-ready { position: absolute; top: 44px; right: 14px; color: var(--moss); transform: rotate(9deg); background: rgba(20,24,12,0.55); animation: bv-q-stamp 0.5s cubic-bezier(0.2,1.6,0.4,1) 0.7s backwards; z-index: 2; }
.bv-q-stamp-paid { color: var(--bone-faint); transform: rotate(-6deg); }

/* ── Odd jobs ───────────────────────────────────────────────────────────── */
.bv-q-jobs { display: flex; flex-direction: column; gap: 12px; }
.bv-q-job {
  position: relative; display: grid; grid-template-columns: 120px 1fr auto; gap: 14px; align-items: center; padding: 12px 14px 12px 12px;
  border: 2px solid #120d08; border-radius: var(--radius-panel); overflow: hidden;
  background: linear-gradient(180deg, rgba(255,235,200,0.06), transparent 45%), linear-gradient(rgba(26,18,8,0.74), rgba(26,18,8,0.86)), url('/clutter/plate.webp') 0 0 / 300px 300px, #1a1208;
  box-shadow: 0 0 0 1px #5a4326, 0 8px 18px rgba(0,0,0,0.6), inset 0 2px 0 rgba(255,222,170,0.14), inset 0 -3px 0 rgba(0,0,0,0.5);
  animation: bv-q-rise 0.5s cubic-bezier(0.22,0.7,0.3,1) backwards; animation-delay: calc(var(--i, 0) * 70ms + 200ms);
  transition: transform 0.18s ease;
}
.bv-q-paid { opacity: 0.6; filter: saturate(0.6); }
/* ── Job art: the real item, floating over a shadow. No medal, no plate. ── */
.bv-q-art { position: relative; display: flex; align-items: center; justify-content: center; gap: 6px; width: 116px; min-height: 100px; }
.bv-q-job-noart { grid-template-columns: 1fr auto; }
.bv-q-item { position: relative; display: grid; place-items: center; width: 100px; height: 100px; }
/* The item floats on a long, slow loop (the img); the hover reaction is on the
   wrapper, so the two transforms never fight over one property. */
.bv-q-float {
  position: relative; z-index: 1; width: 92px; height: 92px; object-fit: contain; pointer-events: none;
  filter: drop-shadow(0 10px 6px rgba(0,0,0,0.55));
  animation: bv-q-float 7s ease-in-out infinite; animation-delay: calc(var(--i, 0) * -1.3s);
  transition: filter 0.5s ease;
}
.bv-q-item { transition: transform 0.7s cubic-bezier(0.25,0.8,0.25,1); }
.bv-q-job:hover .bv-q-item { transform: translateY(-8px) scale(1.14); }
.bv-q-job:hover .bv-q-float { filter: drop-shadow(0 18px 9px rgba(0,0,0,0.5)) drop-shadow(0 0 12px rgba(255,189,92,0.4)); }
/* The only ground: a soft ellipse that shrinks and fades as the item rises. */
.bv-q-shadow {
  position: absolute; left: 50%; bottom: 2px; width: 70px; height: 14px; margin-left: -35px; border-radius: 50%;
  background: radial-gradient(ellipse, rgba(0,0,0,0.6), rgba(0,0,0,0) 70%);
  animation: bv-q-shadow 7s ease-in-out infinite; animation-delay: calc(var(--i, 0) * -1.3s);
  transition: transform 0.7s cubic-bezier(0.25,0.8,0.25,1), opacity 0.7s ease;
}
.bv-q-job:hover .bv-q-shadow { transform: scale(0.72); opacity: 0.6; }
/* Bounty: the face in a framed window beside the item. */
.bv-q-target {
  position: relative; width: 58px; height: 72px; overflow: hidden; border-radius: 6px; border: 2px solid #120d08;
  background: radial-gradient(120% 90% at 50% 20%, #4a2a20, #120a07);
  box-shadow: 0 0 0 2px var(--rust), 0 8px 16px rgba(0,0,0,0.65), inset 0 0 18px rgba(0,0,0,0.7);
}
.bv-q-target-img { width: 100%; height: 100%; object-fit: cover; object-position: top; filter: grayscale(0.4) contrast(1.1); }
@keyframes bv-q-float {
  0%, 100% { transform: translateY(0) rotate(-1.5deg); }
  33% { transform: translateY(-7px) rotate(1deg); }
  66% { transform: translateY(-3px) rotate(2deg); }
}
@keyframes bv-q-shadow {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  33% { transform: scale(0.82); opacity: 0.6; }
  66% { transform: scale(0.92); opacity: 0.75; }
}
.bv-q-job-main { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.bv-q-job-title { margin: 0; font: 800 15px/1.2 var(--font-display); letter-spacing: 0.04em; text-transform: uppercase; color: var(--bone); }
.bv-q-job .bv-q-giver { margin: 0; }
.bv-q-quote {
  margin: 0; padding: 5px 10px; font: italic 12px/1.4 var(--font-note); color: var(--bone-dim);
  background: linear-gradient(rgba(8,5,3,0.78), rgba(8,5,3,0.84)), url('/clutter/plate.webp') 0 0 / 300px 300px; border-left: 3px solid var(--brass); border-radius: 0 var(--radius-well) var(--radius-well) 0;
}
.bv-q-meter { display: flex; flex-direction: column; gap: 5px; padding: 7px 10px; background: linear-gradient(rgba(8,5,3,0.8), rgba(8,5,3,0.86)), url('/clutter/plate.webp') 0 0 / 300px 300px; border: 1px solid rgba(122,90,48,0.4); border-radius: var(--radius-well); box-shadow: inset 0 2px 6px rgba(0,0,0,0.6); }
.bv-q-meter-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.bv-q-meter-task { font: 12px var(--font-technical); color: var(--bone-dim); }
.bv-q-job-side { display: flex; flex-direction: column; align-items: flex-end; justify-content: space-between; gap: 8px; }
.bv-q-job-side .bv-q-pays { flex-direction: column; align-items: flex-end; }
.bv-q-job-side .bv-q-pays-label { display: none; }

/* ── Notes: paper slips pinned to the board ─────────────────────────────── */
.bv-q-slips { display: flex; flex-wrap: wrap; gap: 14px; }
.bv-q-slip {
  position: relative; flex: 1 1 240px; max-width: 360px; padding: 18px 16px 14px; transform: rotate(var(--tilt, -1deg));
  background: linear-gradient(180deg, rgba(255,255,255,0.35), transparent 30%), linear-gradient(168deg, #d9c9a0, #b5a37a);
  border-radius: 2px; box-shadow: 0 8px 14px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.4) inset, inset 0 -10px 18px rgba(90,60,20,0.25);
  animation: bv-q-drop 0.5s cubic-bezier(0.2,1.2,0.4,1) backwards; animation-delay: calc(var(--i, 0) * 100ms + 300ms);
  transition: transform 0.2s ease;
}
.bv-q-slip:hover { transform: rotate(0deg) translateY(-3px) scale(1.02); }
.bv-q-slip-text { margin: 0; font: italic 13px/1.5 var(--font-note); color: #2a1e0e; }
.bv-q-pushpin {
  position: absolute; top: -9px; left: 50%; width: 18px; height: 18px; margin-left: -9px; color: #8a2a1a;
  filter: drop-shadow(0 3px 2px rgba(0,0,0,0.5));
}
.bv-q-pushpin svg { fill: #c04a2a; stroke: #4a1208; }
.bv-q-reset { align-self: flex-start; display: inline-flex; align-items: center; gap: 7px; padding: 5px 12px; background: #150f08; border: 1px solid #5a4326; border-radius: 14px; box-shadow: inset 0 2px 5px rgba(0,0,0,0.75); color: var(--bone-dim); font: 700 11px var(--font-technical); letter-spacing: 0.08em; }

/* ── Empty slots ────────────────────────────────────────────────────────── */
.bv-q-empty {
  display: flex; align-items: center; gap: 12px; margin: 0; padding: 18px 16px; color: var(--bone-dim);
  background: repeating-linear-gradient(135deg, rgba(0,0,0,0.3) 0 8px, rgba(0,0,0,0.18) 8px 16px);
  border: 2px dashed rgba(122,90,48,0.55); border-radius: var(--radius-panel); box-shadow: inset 0 4px 12px rgba(0,0,0,0.6);
  animation: bv-q-rise 0.5s ease backwards;
}
.bv-q-empty-ico { display: inline-flex; width: 28px; height: 28px; flex: none; color: var(--brass); opacity: 0.8; }
.bv-q-empty .bv-q-txt { font: 13px/1.4 var(--font-note); }

/* ── Motion ─────────────────────────────────────────────────────────────── */
@keyframes bv-q-rise { from { opacity: 0; transform: translateY(16px) scale(0.98); } }
@keyframes bv-q-slide { from { opacity: 0; transform: translateX(-12px); } }
@keyframes bv-q-drop { from { opacity: 0; transform: translateY(-22px) rotate(calc(var(--tilt, -1deg) * 4)); } }
@keyframes bv-q-fill { from { transform: scaleX(0); } }
@keyframes bv-q-draw { to { stroke-dashoffset: 0; } }
@keyframes bv-q-stamp { from { opacity: 0; transform: rotate(9deg) scale(2.4); } }
@keyframes bv-q-glow {
  50% { box-shadow: 0 0 0 1px var(--moss), 0 0 22px rgba(120,170,80,0.45), 0 12px 24px rgba(0,0,0,0.65), inset 0 2px 0 rgba(255,222,170,0.16), inset 0 -4px 0 rgba(0,0,0,0.55), inset 0 0 28px rgba(0,0,0,0.5); }
}
@keyframes bv-q-pulse { 50% { filter: brightness(1.18); } }

/* ── The journal window itself ───────────────────────────────────────────
   Wear and clutter rule: the base container is worn metal. Layers on the frame
   (top to bottom): scratches, grime, a dark wash so text stays readable, the
   plate tile. The id selector beats .panel and .bv-win at any source order, so
   a later depth pass cannot flatten it. Backgrounds are on the frame, not on a
   pseudo-element, because the frame scrolls and a pseudo would scroll with it.
   The title lives OUTSIDE the frame, over the scrim, so the frame has less
   height to give. */
#quest-ui.bv-scrim {
  flex-direction: column; gap: 12px; padding: 8px; box-sizing: border-box;
  background:
    radial-gradient(ellipse 70% 62% at 50% 55%, rgba(0,0,0,0.55), transparent 100%),
    rgba(6,4,2,0.78);
}
#quest-ui .bv-win {
  max-height: calc(100dvh - 110px); overscroll-behavior: contain;
  background:
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 420px 420px,
    linear-gradient(rgba(20,14,9,0.74), rgba(14,9,5,0.86)),
    url('/clutter/plate.webp') 0 0 / 420px 420px, #1a1410;
  box-shadow:
    0 0 0 1px #120d08, 0 0 0 2px #5a4326,
    0 0 46px 14px rgba(0,0,0,0.7), 0 22px 50px rgba(0,0,0,0.65),
    inset 0 1px 0 rgba(255,222,170,0.14), inset 0 0 40px rgba(0,0,0,0.55);
}
#quest-ui .bv-win-close { top: 2px; right: 4px; }
.bv-q-wintitle { flex: none; max-width: 94vw; text-align: center; pointer-events: none; }
.bv-q-wintitle-text {
  margin: 0; font: 800 clamp(1.4rem, 5vw, 2rem)/1.05 var(--font-display); letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--brass-hot);
  text-shadow: 0 2px 0 rgba(0,0,0,0.9), 0 0 18px rgba(255,189,92,0.35), 0 3px 14px rgba(0,0,0,0.9);
}
.bv-q-wintitle-sub {
  margin: 4px 0 0; font: 700 0.72rem var(--font-technical); letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--bone-dim); text-shadow: 0 1px 3px rgba(0,0,0,0.95);
}
/* Phones: the frame takes the width, the tiles shrink, the scratch layer goes. */
@media (max-width: 640px) {
  #quest-ui.bv-scrim { gap: 8px; padding: 6px; }
  #quest-ui .bv-win {
    max-height: calc(100dvh - 88px);
    background:
      url('/clutter/grime.webp') 30% 20% / 260px 260px,
      linear-gradient(rgba(20,14,9,0.78), rgba(14,9,5,0.88)),
      url('/clutter/plate.webp') 0 0 / 260px 260px, #1a1410;
    box-shadow: 0 0 0 1px #120d08, 0 0 0 2px #5a4326, 0 0 28px 8px rgba(0,0,0,0.7), inset 0 0 28px rgba(0,0,0,0.55);
  }
}

@media (max-width: 640px) {
  .bv-q-strip { grid-template-columns: 1fr; }
  .bv-q-cards { grid-template-columns: 1fr; }
  .bv-q-job { grid-template-columns: 76px 1fr; }
  .bv-q-job-noart { grid-template-columns: 1fr; }
  .bv-q-art { width: 70px; }
  .bv-q-float { width: 64px; height: 64px; }
  .bv-q-item { width: 70px; height: 72px; }
  .bv-q-job-side { grid-column: 1 / -1; flex-direction: row; align-items: center; justify-content: space-between; }
  .bv-q-job-side .bv-q-pays { flex-direction: row; align-items: center; }
}
@media (prefers-reduced-motion: reduce) {
  .bv-q-float, .bv-q-shadow, .bv-q-heading { animation: none; }
  .bv-q-item, .bv-q-shadow { transition: none; }
  .bv-q-gauge, .bv-q-card, .bv-q-job, .bv-q-obj, .bv-q-fill, .bv-q-slip, .bv-q-empty, .bv-q-stamp-ready,
  .bv-q-ready, .bv-q-btn-moss:not(:disabled), .bv-q-obj-done .bv-q-checkbox svg { animation: none; }
  .bv-q-obj-done .bv-q-checkbox svg { stroke-dashoffset: 0; }
  .bv-q-card, .bv-q-job, .bv-q-btn, .bv-q-chip, .bv-q-slip { transition: none; }
}
`;
